/**
 * Shared building blocks for persona landing pages.
 *
 * Each persona page (CtoLandingPage, CisoLandingPage, ...) renders the
 * same skeleton:
 *   <LandingHeader/>           — title, persona switcher, refresh
 *   <HeroTiles>                — 4-6 big band-coloured numbers
 *   <TwoColumnBody>            — left: attention-now; right: trends
 *   <StickyFooter>             — quick-action buttons
 *
 * Pulling these into one file keeps each page <500 lines and gives the
 * five pages a uniform look. All data comes from the parent — these are
 * presentation primitives only.
 */
import { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight, RefreshCw, ExternalLink } from 'lucide-react'
import { clsx } from 'clsx'

// ── Persona switcher chip-row used by every landing page ────────────────────
// Order mirrors backend/api/personas.py — the natural progression of a
// banking-IT org chart: strategy → security → compliance → ops → support
// → platform.
const PERSONAS: { id: string; label: string }[] = [
  { id: 'cto',         label: 'CTO' },
  { id: 'cio',         label: 'CIO' },
  { id: 'ciso',        label: 'CISO' },
  { id: 'compliance',  label: 'Compliance' },
  { id: 'soc',         label: 'SOC' },
  { id: 'sdlc',        label: 'SDLC' },
  { id: 'hosting',     label: 'Hosting' },
  { id: 'helpdesk',    label: 'Helpdesk' },
  { id: 'devops',      label: 'DevOps' },
]

export function PersonaSwitcher({ active }: { active: string }) {
  return (
    <div className="flex flex-wrap items-center gap-1 text-[10px]">
      {PERSONAS.map(p => (
        <Link key={p.id} to={`/app/landing/${p.id}`}
          className={clsx(
            'px-2 py-0.5 rounded border',
            p.id === active
              ? 'bg-blue/15 border-blue/40 text-blue-light font-semibold'
              : 'border-border text-text-muted hover:text-text-primary hover:bg-surface-raised',
          )}>
          {p.label}
        </Link>
      ))}
      <Link to="/app/landing"
        className="px-2 py-0.5 rounded border border-border text-text-muted hover:text-text-primary hover:bg-surface-raised">
        change
      </Link>
    </div>
  )
}

// ── Header ──────────────────────────────────────────────────────────────────
export function LandingHeader({
  title, subtitle, persona, onRefresh, refreshing,
}: {
  title: string
  subtitle: string
  persona: string
  onRefresh: () => void
  refreshing: boolean
}) {
  return (
    <div className="flex items-baseline gap-3 pb-2 border-b border-border flex-wrap">
      <div>
        <h1 className="text-lg font-bold text-text-primary">{title}</h1>
        <p className="text-[10px] text-text-muted uppercase tracking-widest">{subtitle}</p>
      </div>
      <div className="ml-auto flex items-center gap-2">
        <PersonaSwitcher active={persona} />
        <button type="button" onClick={onRefresh} disabled={refreshing} className="btn-ghost">
          <RefreshCw size={11} className={refreshing ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>
    </div>
  )
}

// ── Hero tile — the big band-coloured number for the top strip ──────────────
export type HeroTone = 'rose' | 'amber' | 'cyan' | 'emerald' | 'blue' | 'purple' | 'muted'

const TONE_BG: Record<HeroTone, string> = {
  rose:    'bg-rose/10 border-rose/30 text-rose-light',
  amber:   'bg-amber/10 border-amber/30 text-amber-light',
  cyan:    'bg-cyan/10 border-cyan/30 text-cyan',
  emerald: 'bg-emerald/10 border-emerald/30 text-emerald',
  blue:    'bg-blue/10 border-blue/30 text-blue-light',
  purple:  'bg-purple/10 border-purple/30 text-purple-light',
  muted:   'bg-surface-raised border-border text-text-secondary',
}

export function HeroTile({
  label, value, tone, sub, to,
}: {
  label: string
  value: string | number
  tone: HeroTone
  sub?: string
  to?: string
}) {
  const inner = (
    <>
      <div className="text-[10px] uppercase tracking-widest opacity-80">{label}</div>
      <div className="text-2xl font-black font-mono mt-1 leading-tight">{value}</div>
      {sub && <div className="text-[9px] opacity-70 mt-0.5">{sub}</div>}
    </>
  )
  const base = clsx('card p-3 border', TONE_BG[tone], to && 'hover:ring-1 hover:ring-blue/40 transition')
  return to ? <Link to={to} className={base}>{inner}</Link> : <div className={base}>{inner}</div>
}

// ── Two-column body ─────────────────────────────────────────────────────────
export function TwoColumnBody({ left, right }: { left: ReactNode; right: ReactNode }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="space-y-3">{left}</div>
      <div className="space-y-3">{right}</div>
    </div>
  )
}

// ── Panel — wraps an attention-now or trend card ────────────────────────────
export function Panel({
  title, viewAllTo, viewAllLabel, children,
}: {
  title: string
  viewAllTo?: string
  viewAllLabel?: string
  children: ReactNode
}) {
  return (
    <div className="card p-3">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-[11px] uppercase tracking-widest text-text-secondary font-semibold">{title}</h2>
        {viewAllTo && (
          <Link to={viewAllTo} className="text-[10px] text-blue-light hover:underline inline-flex items-center gap-0.5">
            {viewAllLabel || 'view all'} <ChevronRight size={10} />
          </Link>
        )}
      </div>
      {children}
    </div>
  )
}

// ── Empty state — graceful "no data yet" with a configure link ─────────────
export function EmptyState({
  message, configureTo, configureLabel,
}: {
  message: string
  configureTo?: string
  configureLabel?: string
}) {
  return (
    <div className="text-center py-4 px-2 text-[11px] text-text-muted">
      <div>{message}</div>
      {configureTo && (
        <Link to={configureTo} className="text-blue-light hover:underline mt-1 inline-flex items-center gap-0.5">
          {configureLabel || 'configure'} <ExternalLink size={10} />
        </Link>
      )}
    </div>
  )
}

// ── Sticky footer with quick-action buttons ─────────────────────────────────
export interface FooterAction {
  label: string
  to?: string
  onClick?: () => void
  icon?: React.ElementType
  tone?: 'primary' | 'secondary' | 'ghost'
}

export function StickyFooter({ actions }: { actions: FooterAction[] }) {
  return (
    <div className="sticky bottom-0 -mx-2 px-2 py-2 bg-surface/95 backdrop-blur border-t border-border z-10">
      <div className="flex flex-wrap items-center justify-end gap-2">
        {actions.map((a, i) => {
          const Icon = a.icon
          const cls = clsx(
            'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded text-[11px] font-semibold border transition',
            a.tone === 'primary'
              ? 'bg-blue text-white border-blue hover:bg-blue/90'
              : a.tone === 'secondary'
                ? 'bg-emerald/10 text-emerald border-emerald/30 hover:bg-emerald/20'
                : 'bg-surface-card text-text-primary border-border hover:bg-surface-raised',
          )
          if (a.to) return <Link key={i} to={a.to} className={cls}>{Icon && <Icon size={11} />}{a.label}</Link>
          return (
            <button key={i} type="button" onClick={a.onClick} className={cls}>
              {Icon && <Icon size={11} />}{a.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ── Tiny helper — wrap each list item with a hover-state row ────────────────
export function ListRow({
  primary, secondary, right, to,
}: {
  primary: ReactNode
  secondary?: ReactNode
  right?: ReactNode
  to?: string
}) {
  const body = (
    <div className="flex items-center justify-between gap-2 py-1.5 px-1.5 rounded hover:bg-surface-raised/40 text-[11px]">
      <div className="min-w-0 flex-1">
        <div className="text-text-primary truncate">{primary}</div>
        {secondary && <div className="text-[10px] text-text-muted truncate">{secondary}</div>}
      </div>
      {right && <div className="shrink-0 text-[10px] font-mono text-text-secondary">{right}</div>}
    </div>
  )
  return to ? <Link to={to}>{body}</Link> : body
}

// ── safeNum — graceful number-or-fallback for hero tiles ────────────────────
export function safeNum(v: unknown, fallback: string = '—'): string {
  if (v === undefined || v === null) return fallback
  if (typeof v === 'number' && !Number.isFinite(v)) return fallback
  if (typeof v === 'number') return v.toLocaleString()
  return String(v)
}
